import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const PriceElasticity = () => {
  // State variables for Price Elasticity Calculation
  const [initialPrice, setInitialPrice] = useState(0); // Initial price
  const [finalPrice, setFinalPrice] = useState(0); // Final price
  const [initialQuantity, setInitialQuantity] = useState(0); // Initial quantity demanded
  const [finalQuantity, setFinalQuantity] = useState(0); // Final quantity demanded
  const [elasticityResult, setElasticityResult] = useState(0); // Calculated elasticity
  const [percentageChangePrice, setPercentageChangePrice] = useState(0); // Change in price
  const [percentageChangeQuantity, setPercentageChangeQuantity] = useState(0); // Change in quantity

  // Event handlers to update state values
  const handleInitialPriceChange = (e) =>
    setInitialPrice(parseFloat(e.target.value) || 0);
  const handleFinalPriceChange = (e) =>
    setFinalPrice(parseFloat(e.target.value) || 0);
  const handleInitialQuantityChange = (e) =>
    setInitialQuantity(parseFloat(e.target.value) || 0);
  const handleFinalQuantityChange = (e) =>
    setFinalQuantity(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate price elasticity when inputs change
  useEffect(() => {
    if (
      initialPrice > 0 &&
      finalPrice > 0 &&
      initialQuantity > 0 &&
      finalQuantity > 0
    ) {
      // Calculate percentage change in price and quantity
      const priceChange = ((finalPrice - initialPrice) / initialPrice) * 100;
      const quantityChange =
        ((finalQuantity - initialQuantity) / initialQuantity) * 100;

      // Calculate price elasticity
      const elasticity = Math.abs(quantityChange / priceChange);

      setPercentageChangePrice(priceChange);
      setPercentageChangeQuantity(quantityChange);
      setElasticityResult(elasticity);
    } else {
      setElasticityResult(0);
      setPercentageChangePrice(0);
      setPercentageChangeQuantity(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [initialPrice, finalPrice, initialQuantity, finalQuantity]);

  return (
    <Layout
      title="Price Elasticity of Demand Calculator"
      path="price-elasticity"
    >
      <section className="section-padding" id="price-elasticity-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Price Elasticity of Demand Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Price Elasticity of Demand Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Initial Price Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="initialPrice" className="form-label">
                        Initial Price
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Initial Price:</strong> Enter the original price of the product."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="initialPrice"
                        placeholder="Enter initial price"
                        value={initialPrice || ""}
                        onChange={handleInitialPriceChange}
                      />
                    </div>

                    {/* Final Price Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="finalPrice" className="form-label">
                        Final Price
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Final Price:</strong> Enter the new price after a price change."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="finalPrice"
                        placeholder="Enter final price"
                        value={finalPrice || ""}
                        onChange={handleFinalPriceChange}
                      />
                    </div>

                    {/* Initial Quantity Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="initialQuantity" className="form-label">
                        Initial Quantity Demanded
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Initial Quantity Demanded:</strong> Enter the quantity of the product demanded before the price change."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="initialQuantity"
                        placeholder="Enter initial quantity demanded"
                        value={initialQuantity || ""}
                        onChange={handleInitialQuantityChange}
                      />
                    </div>

                    {/* Final Quantity Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="finalQuantity" className="form-label">
                        Final Quantity Demanded
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Final Quantity Demanded:</strong> Enter the quantity demanded after the price change."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="finalQuantity"
                        placeholder="Enter final quantity demanded"
                        value={finalQuantity || ""}
                        onChange={handleFinalQuantityChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Price Elasticity:{" "}
                        <span className="fw-bold ms-3">
                          {elasticityResult === 0
                            ? "0.00"
                            : elasticityResult.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </span>
                      </h6>
                      <p>
                        Percentage Change in Price:{" "}
                        <strong>
                          {percentageChangePrice.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                          %
                        </strong>
                      </p>
                      <p>
                        Percentage Change in Quantity Demanded:{" "}
                        <strong>
                          {percentageChangeQuantity.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                          %
                        </strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Price Elasticity of Demand Calculator
                </h3>

                {/* What is a Price Elasticity of Demand Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Price Elasticity of Demand Calculator?
                </h4>
                <p>
                  The **Price Elasticity of Demand (PED) Calculator** helps
                  businesses and economists measure how sensitive the demand for
                  a product is to changes in its price. This is useful in
                  understanding consumer behavior and adjusting pricing
                  strategies accordingly.
                </p>

                {/* Why Use a Price Elasticity of Demand Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Price Elasticity of Demand Calculator?
                </h4>
                <p>
                  Price elasticity allows businesses to determine whether
                  increasing or decreasing prices will lead to higher or lower
                  demand. It helps optimize pricing strategies to maximize
                  revenue and avoid overpricing or underpricing.
                </p>

                {/* Benefits of Using a Price Elasticity of Demand Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Price Elasticity of Demand Calculator
                </h4>
                <ul>
                  <li>
                    <strong>
                      Helps businesses understand demand sensitivity:
                    </strong>{" "}
                    By calculating elasticity, companies can predict the impact
                    of price changes on sales volume.
                  </li>
                  <li>
                    <strong>Supports dynamic pricing strategies:</strong> It
                    helps adjust prices in response to market conditions and
                    consumer demand patterns.
                  </li>
                  <li>
                    <strong>Useful for revenue optimization:</strong> Price
                    elasticity insights enable businesses to find the optimal
                    price point for maximum profitability.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Price Elasticity of Demand Calculator?
                </h4>
                <p>The Price Elasticity of Demand Calculator is ideal for:</p>
                <ul>
                  <li>
                    <strong>Retailers:</strong> To determine optimal pricing for
                    products and maximize sales revenue.
                  </li>
                  <li>
                    <strong>E-commerce businesses:</strong> To adapt prices
                    based on consumer demand elasticity and optimize online
                    sales.
                  </li>
                  <li>
                    <strong>Marketing teams:</strong> To forecast demand
                    responses to price changes and adjust marketing strategies.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How Does Price Elasticity Affect Revenue?
                </h4>
                <p>
                  If demand is elastic (elasticity ${">"} 1), lowering prices
                  can increase total revenue. If demand is inelastic (elasticity
                  ${"<"} 1), raising prices may lead to higher revenue.
                </p>

                <h4 className="mt-4 mb-3">
                  Can Price Elasticity Be Different for Different Products?
                </h4>
                <p>
                  Yes, price elasticity can vary significantly between products.
                  Luxury items, for example, tend to have more elastic demand,
                  while necessities tend to have inelastic demand.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PriceElasticity;
